import { parseIonicSpecies } from './ionicEquation';

/**
 * Batteries mode for the Electrochemical Cells Lab (study-chemistry-tools-prd.md §8.3). Reviewed
 * primary, secondary and fuel cells with their electrode half-equations, discharge or recharge
 * direction and cell category. All answer truth lives here, not in JSX.
 */

export type BatteryCategory = 'primary' | 'secondary' | 'fuel-cell';
export type BatteryDirection = 'discharge' | 'recharge';
export type ElectrodeSign = 'negative' | 'positive';
export type BatteryEnergyChange = 'chemical-to-electrical' | 'electrical-to-chemical';

export const BATTERY_REVIEW_STATUS = 'pending-teaching-review' as const;

export const BATTERY_SOURCES = {
  linden: {
    id: 'linden',
    citation:
      'Reddy, T. B. (ed.), Linden’s Handbook of Batteries, 4th ed., McGraw-Hill: electrode reactions and nominal cell voltages of alkaline, silver oxide, lead–acid, NiMH and lithium-ion cells.',
  },
  crc: {
    id: 'crc',
    citation:
      'Vanýsek, P. “Electrochemical Series”, CRC Handbook of Chemistry and Physics: O₂/H₂O couple E° = 1.229 V, giving E°cell = 1.23 V for the hydrogen–oxygen fuel cell at 298 K.',
  },
  textbook: {
    id: 'textbook',
    citation:
      'Standard senior chemistry treatment of primary, secondary and fuel cells (discharge and recharge half-equations, electrode polarity).',
  },
} as const;

export type BatterySourceId = keyof typeof BATTERY_SOURCES;

export const BATTERY_CONDITIONS =
  'Nominal voltages are typical values for a single cell at room temperature (about 25 °C). The fuel-cell value is the standard cell potential E°cell at 25 °C and 100 kPa; working cells deliver less under load.';

export const BATTERY_CATEGORY_LABELS: Record<BatteryCategory, string> = {
  primary: 'Primary cell',
  secondary: 'Secondary (rechargeable) cell',
  'fuel-cell': 'Fuel cell',
};

export const ELECTRODE_SIGN_LABELS: Record<ElectrodeSign, string> = {
  negative: 'Negative electrode (−)',
  positive: 'Positive electrode (+)',
};

/* ------------------------------------------------------------------ species */

interface BatterySpeciesSeed {
  ascii: string;
  display: string;
  /** Only for the generic hydrogen-storing alloy “M”, which is not a single element symbol. */
  elements?: Record<string, number>;
}

export interface BatterySpecies {
  id: BatterySpeciesId;
  display: string;
  elements: Record<string, number>;
  charge: number;
}

const BATTERY_SPECIES_SEEDS = {
  Zn: { ascii: 'Zn(s)', display: 'Zn(s)' },
  ZnO: { ascii: 'ZnO(s)', display: 'ZnO(s)' },
  OH: { ascii: 'OH-(aq)', display: 'OH⁻(aq)' },
  H2O: { ascii: 'H2O(l)', display: 'H₂O(l)' },
  MnO2: { ascii: 'MnO2(s)', display: 'MnO₂(s)' },
  Mn2O3: { ascii: 'Mn2O3(s)', display: 'Mn₂O₃(s)' },
  Ag2O: { ascii: 'Ag2O(s)', display: 'Ag₂O(s)' },
  Ag: { ascii: 'Ag(s)', display: 'Ag(s)' },
  Pb: { ascii: 'Pb(s)', display: 'Pb(s)' },
  PbO2: { ascii: 'PbO2(s)', display: 'PbO₂(s)' },
  PbSO4: { ascii: 'PbSO4(s)', display: 'PbSO₄(s)' },
  SO4: { ascii: 'SO4^2-(aq)', display: 'SO₄²⁻(aq)' },
  H: { ascii: 'H+(aq)', display: 'H⁺(aq)' },
  H2: { ascii: 'H2(g)', display: 'H₂(g)' },
  O2: { ascii: 'O2(g)', display: 'O₂(g)' },
  LiC6: { ascii: 'LiC6(s)', display: 'LiC₆(s)' },
  C6: { ascii: 'C6(s)', display: 'C₆(s)' },
  Li: { ascii: 'Li+', display: 'Li⁺' },
  CoO2: { ascii: 'CoO2(s)', display: 'CoO₂(s)' },
  LiCoO2: { ascii: 'LiCoO2(s)', display: 'LiCoO₂(s)' },
  MH: { ascii: 'MH(s)', display: 'MH(s)', elements: { M: 1, H: 1 } },
  M: { ascii: 'M(s)', display: 'M(s)', elements: { M: 1 } },
  NiOOH: { ascii: 'NiO(OH)(s)', display: 'NiO(OH)(s)' },
  NiOH2: { ascii: 'Ni(OH)2(s)', display: 'Ni(OH)₂(s)' },
} satisfies Record<string, BatterySpeciesSeed>;

export type BatterySpeciesId = keyof typeof BATTERY_SPECIES_SEEDS;

function buildBatterySpecies(id: BatterySpeciesId, seed: BatterySpeciesSeed): BatterySpecies {
  if (seed.elements) {
    return { id, display: seed.display, elements: { ...seed.elements }, charge: 0 };
  }
  const parsed = parseIonicSpecies(seed.ascii);
  if (!parsed.species) {
    throw new Error(`Battery species ${id} failed to parse: ${seed.ascii}`);
  }
  return {
    id,
    display: seed.display,
    elements: { ...parsed.species.atoms },
    charge: parsed.species.charge,
  };
}

export const BATTERY_SPECIES = Object.fromEntries(
  (Object.entries(BATTERY_SPECIES_SEEDS) as [BatterySpeciesId, BatterySpeciesSeed][]).map(
    ([id, seed]) => [id, buildBatterySpecies(id, seed)],
  ),
) as Record<BatterySpeciesId, BatterySpecies>;

/* ---------------------------------------------------------------- equations */

export interface BatteryTerm {
  species: BatterySpeciesId;
  coefficient: number;
}

export interface BatteryHalfEquation {
  left: BatteryTerm[];
  right: BatteryTerm[];
  electrons: number;
  electronSide: 'left' | 'right';
}

export interface BatteryFullEquation {
  left: BatteryTerm[];
  right: BatteryTerm[];
}

const t = (species: BatterySpeciesId, coefficient = 1): BatteryTerm => ({ species, coefficient });
const oxidation = (left: BatteryTerm[], right: BatteryTerm[], electrons: number): BatteryHalfEquation => ({
  left,
  right,
  electrons,
  electronSide: 'right',
});
const reduction = (left: BatteryTerm[], right: BatteryTerm[], electrons: number): BatteryHalfEquation => ({
  left,
  right,
  electrons,
  electronSide: 'left',
});

/* -------------------------------------------------------------------- cases */

export interface BatteryVoltage {
  valueV: number;
  label: string;
}

export interface BatteryCase {
  /** Stable id. Never rename: saved bindings may reference it. */
  id: string;
  name: string;
  everydayUse: string;
  category: BatteryCategory;
  direction: BatteryDirection;
  /** The same cell running the other way, when the tool shows both directions. */
  pairedCaseId?: string;
  electrolyte: string;
  negativeMaterial: string;
  positiveMaterial: string;
  /** Electrode where oxidation happens in this direction. */
  anodeElectrode: ElectrodeSign;
  anode: BatteryHalfEquation;
  cathode: BatteryHalfEquation;
  overall: BatteryFullEquation;
  voltage: BatteryVoltage;
  /** External device in the circuit: a load (lamp, phone) or a charger. */
  externalDevice: string;
  categoryReason: string;
  ionFlow: string;
  energyChange: BatteryEnergyChange;
  sources: BatterySourceId[];
  reviewStatus: typeof BATTERY_REVIEW_STATUS;
}

const ZINC_IN_HYDROXIDE = oxidation([t('Zn'), t('OH', 2)], [t('ZnO'), t('H2O')], 2);
const LEAD_DISCHARGE_NEGATIVE = oxidation([t('Pb'), t('SO4')], [t('PbSO4')], 2);
const LEAD_DISCHARGE_POSITIVE = reduction(
  [t('PbO2'), t('H', 4), t('SO4')],
  [t('PbSO4'), t('H2O', 2)],
  2,
);
const LITHIUM_DISCHARGE_NEGATIVE = oxidation([t('LiC6')], [t('C6'), t('Li')], 1);
const LITHIUM_DISCHARGE_POSITIVE = reduction([t('CoO2'), t('Li')], [t('LiCoO2')], 1);

/** Reverses a half-equation: an oxidation becomes the matching reduction and vice versa. */
export function reverseBatteryHalf(half: BatteryHalfEquation): BatteryHalfEquation {
  return {
    left: half.right,
    right: half.left,
    electrons: half.electrons,
    electronSide: half.electronSide === 'left' ? 'right' : 'left',
  };
}

export const BATTERY_CASES: BatteryCase[] = [
  {
    id: 'alkaline-zn-mno2',
    name: 'Alkaline cell (Zn/MnO₂)',
    everydayUse: 'AA and AAA batteries in remotes, torches and toys',
    category: 'primary',
    direction: 'discharge',
    electrolyte: 'Potassium hydroxide (KOH) paste',
    negativeMaterial: 'Zinc powder',
    positiveMaterial: 'Manganese dioxide mixed with carbon',
    anodeElectrode: 'negative',
    anode: ZINC_IN_HYDROXIDE,
    cathode: reduction([t('MnO2', 2), t('H2O')], [t('Mn2O3'), t('OH', 2)], 2),
    overall: { left: [t('Zn'), t('MnO2', 2)], right: [t('ZnO'), t('Mn2O3')] },
    voltage: { valueV: 1.5, label: 'about 1.5 V (nominal)' },
    externalDevice: 'Load (torch globe)',
    categoryReason:
      'An alkaline cell is a primary cell: the zinc and manganese dioxide are used up and the products do not reform reliably when current is pushed back, so a flat cell is recycled, not recharged. Trying to recharge it can make it leak or rupture.',
    ionFlow:
      'OH⁻ ions carry charge through the KOH paste: they form at the cathode and are used at the zinc anode.',
    energyChange: 'chemical-to-electrical',
    sources: ['linden', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'silver-oxide-button',
    name: 'Silver oxide button cell (Zn/Ag₂O)',
    everydayUse: 'Watches, calculators and small medical devices',
    category: 'primary',
    direction: 'discharge',
    electrolyte: 'Potassium hydroxide (KOH) solution held in a separator',
    negativeMaterial: 'Zinc powder',
    positiveMaterial: 'Silver(I) oxide',
    anodeElectrode: 'negative',
    anode: ZINC_IN_HYDROXIDE,
    cathode: reduction([t('Ag2O'), t('H2O')], [t('Ag', 2), t('OH', 2)], 2),
    overall: { left: [t('Zn'), t('Ag2O')], right: [t('ZnO'), t('Ag', 2)] },
    voltage: { valueV: 1.55, label: 'about 1.55 V (nominal)' },
    externalDevice: 'Load (watch circuit)',
    categoryReason:
      'A silver oxide button cell is a primary cell: it gives a very steady voltage while the zinc and silver oxide are used up, and it is not designed to be recharged.',
    ionFlow:
      'OH⁻ ions carry charge through the electrolyte: they form at the silver oxide cathode and are used at the zinc anode.',
    energyChange: 'chemical-to-electrical',
    sources: ['linden', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'lead-acid-discharge',
    name: 'Lead–acid cell: discharging',
    everydayUse: 'Starting a car engine (six cells make a 12 V battery)',
    category: 'secondary',
    direction: 'discharge',
    pairedCaseId: 'lead-acid-recharge',
    electrolyte: 'Sulfuric acid (H₂SO₄) solution',
    negativeMaterial: 'Lead (Pb) plate',
    positiveMaterial: 'Lead(IV) oxide (PbO₂) plate',
    anodeElectrode: 'negative',
    anode: LEAD_DISCHARGE_NEGATIVE,
    cathode: LEAD_DISCHARGE_POSITIVE,
    overall: {
      left: [t('Pb'), t('PbO2'), t('H', 4), t('SO4', 2)],
      right: [t('PbSO4', 2), t('H2O', 2)],
    },
    voltage: { valueV: 2, label: 'about 2.0 V per cell (nominal)' },
    externalDevice: 'Load (starter motor)',
    categoryReason:
      'A lead–acid cell is a secondary cell: the product, lead(II) sulfate, stays on both plates as a solid, so a charger can drive both half-reactions backwards.',
    ionFlow:
      'H⁺ and SO₄²⁻ ions from the acid are used at the plates, so the acid becomes more dilute (its density falls) as the cell discharges.',
    energyChange: 'chemical-to-electrical',
    sources: ['linden', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'lead-acid-recharge',
    name: 'Lead–acid cell: recharging',
    everydayUse: 'The car’s alternator or a battery charger restoring the battery',
    category: 'secondary',
    direction: 'recharge',
    pairedCaseId: 'lead-acid-discharge',
    electrolyte: 'Sulfuric acid (H₂SO₄) solution',
    negativeMaterial: 'Lead (Pb) plate, coated in PbSO₄',
    positiveMaterial: 'Lead(IV) oxide (PbO₂) plate, coated in PbSO₄',
    anodeElectrode: 'positive',
    anode: reverseBatteryHalf(LEAD_DISCHARGE_POSITIVE),
    cathode: reverseBatteryHalf(LEAD_DISCHARGE_NEGATIVE),
    overall: {
      left: [t('PbSO4', 2), t('H2O', 2)],
      right: [t('Pb'), t('PbO2'), t('H', 4), t('SO4', 2)],
    },
    voltage: {
      valueV: 2,
      label: 'the charger must push harder than the cell’s 2.0 V (car systems use about 14 V for six cells)',
    },
    externalDevice: 'Charger (power supply)',
    categoryReason:
      'This is the same secondary cell running backwards: electrical energy from the charger turns PbSO₄ back into Pb and PbO₂ and regenerates the sulfuric acid.',
    ionFlow:
      'H⁺ and SO₄²⁻ ions are released back into solution, so the acid becomes more concentrated again.',
    energyChange: 'electrical-to-chemical',
    sources: ['linden', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'lithium-ion-discharge',
    name: 'Lithium-ion cell: discharging',
    everydayUse: 'Phones, laptops and electric vehicles',
    category: 'secondary',
    direction: 'discharge',
    pairedCaseId: 'lithium-ion-recharge',
    electrolyte: 'A lithium salt (such as LiPF₆) in an organic solvent — no water',
    negativeMaterial: 'Graphite holding lithium (LiC₆)',
    positiveMaterial: 'Cobalt oxide layers (CoO₂ / LiCoO₂)',
    anodeElectrode: 'negative',
    anode: LITHIUM_DISCHARGE_NEGATIVE,
    cathode: LITHIUM_DISCHARGE_POSITIVE,
    overall: { left: [t('LiC6'), t('CoO2')], right: [t('C6'), t('LiCoO2')] },
    voltage: { valueV: 3.7, label: 'about 3.7 V (nominal)' },
    externalDevice: 'Load (phone circuit)',
    categoryReason:
      'A lithium-ion cell is a secondary cell: Li⁺ ions slide in and out of the graphite and cobalt oxide layers (intercalation) without destroying either structure, so the reaction can be reversed hundreds of times.',
    ionFlow:
      'Li⁺ ions move through the non-aqueous electrolyte from the graphite anode to the cobalt oxide cathode, while electrons take the external circuit.',
    energyChange: 'chemical-to-electrical',
    sources: ['linden', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'lithium-ion-recharge',
    name: 'Lithium-ion cell: recharging',
    everydayUse: 'Plugging in a phone or laptop',
    category: 'secondary',
    direction: 'recharge',
    pairedCaseId: 'lithium-ion-discharge',
    electrolyte: 'A lithium salt (such as LiPF₆) in an organic solvent — no water',
    negativeMaterial: 'Graphite (C₆) layers',
    positiveMaterial: 'Lithium cobalt oxide (LiCoO₂)',
    anodeElectrode: 'positive',
    anode: reverseBatteryHalf(LITHIUM_DISCHARGE_POSITIVE),
    cathode: reverseBatteryHalf(LITHIUM_DISCHARGE_NEGATIVE),
    overall: { left: [t('C6'), t('LiCoO2')], right: [t('LiC6'), t('CoO2')] },
    voltage: { valueV: 4.2, label: 'chargers stop at about 4.2 V per cell' },
    externalDevice: 'Charger (power supply)',
    categoryReason:
      'This is the same secondary cell running backwards: the charger drives Li⁺ ions out of LiCoO₂ and back between the graphite layers, storing chemical energy again.',
    ionFlow:
      'Li⁺ ions are driven back through the electrolyte from the cobalt oxide into the graphite layers.',
    energyChange: 'electrical-to-chemical',
    sources: ['linden', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'nimh-discharge',
    name: 'Nickel–metal hydride (NiMH) cell',
    everydayUse: 'Rechargeable AA batteries and many hybrid cars',
    category: 'secondary',
    direction: 'discharge',
    electrolyte: 'Potassium hydroxide (KOH) solution',
    negativeMaterial: 'Hydrogen-storing metal alloy (MH)',
    positiveMaterial: 'Nickel oxyhydroxide (NiO(OH))',
    anodeElectrode: 'negative',
    anode: oxidation([t('MH'), t('OH')], [t('M'), t('H2O')], 1),
    cathode: reduction([t('NiOOH'), t('H2O')], [t('NiOH2'), t('OH')], 1),
    overall: { left: [t('MH'), t('NiOOH')], right: [t('M'), t('NiOH2')] },
    voltage: { valueV: 1.2, label: 'about 1.2 V (nominal)' },
    externalDevice: 'Load (camera flash)',
    categoryReason:
      'A NiMH cell is a secondary cell: the metal hydride and nickel oxyhydroxide reform when a charger reverses the current, and all the solids stay on their electrodes.',
    ionFlow:
      'OH⁻ ions carry charge through the KOH electrolyte: they form at the nickel cathode and are used at the metal hydride anode. “M” stands for a reviewed alloy, not one element.',
    energyChange: 'chemical-to-electrical',
    sources: ['linden', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'hydrogen-fuel-cell-acidic',
    name: 'Hydrogen fuel cell (acidic electrolyte)',
    everydayUse: 'Fuel-cell cars and buses (proton-exchange membrane cells)',
    category: 'fuel-cell',
    direction: 'discharge',
    electrolyte: 'Acidic: a proton-exchange membrane or phosphoric acid',
    negativeMaterial: 'Porous catalyst electrode (platinum) — hydrogen fed in',
    positiveMaterial: 'Porous catalyst electrode (platinum) — oxygen or air fed in',
    anodeElectrode: 'negative',
    anode: oxidation([t('H2')], [t('H', 2)], 2),
    cathode: reduction([t('O2'), t('H', 4)], [t('H2O', 2)], 4),
    overall: { left: [t('H2', 2), t('O2')], right: [t('H2O', 2)] },
    voltage: { valueV: 1.23, label: 'E°cell = 1.23 V (standard, 25 °C)' },
    externalDevice: 'Load (electric motor)',
    categoryReason:
      'A fuel cell is neither primary nor secondary: hydrogen and oxygen are supplied continuously and water leaves the cell, so it keeps working while fuel flows in. It is refuelled, not recharged.',
    ionFlow:
      'H⁺ ions move through the acidic electrolyte from the hydrogen anode to the oxygen cathode, where they form water.',
    energyChange: 'chemical-to-electrical',
    sources: ['crc', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
  {
    id: 'hydrogen-fuel-cell-alkaline',
    name: 'Hydrogen fuel cell (alkaline electrolyte)',
    everydayUse: 'Alkaline fuel cells used on spacecraft',
    category: 'fuel-cell',
    direction: 'discharge',
    electrolyte: 'Alkaline: potassium hydroxide (KOH) solution',
    negativeMaterial: 'Porous catalyst electrode — hydrogen fed in',
    positiveMaterial: 'Porous catalyst electrode — oxygen fed in',
    anodeElectrode: 'negative',
    anode: oxidation([t('H2'), t('OH', 2)], [t('H2O', 2)], 2),
    cathode: reduction([t('O2'), t('H2O', 2)], [t('OH', 4)], 4),
    overall: { left: [t('H2', 2), t('O2')], right: [t('H2O', 2)] },
    voltage: { valueV: 1.23, label: 'E°cell = 1.23 V (standard, 25 °C)' },
    externalDevice: 'Load (electric motor)',
    categoryReason:
      'A fuel cell is neither primary nor secondary: hydrogen and oxygen are supplied continuously and water leaves the cell. Changing the electrolyte from acidic to alkaline changes the half-equations but not the overall reaction or E°cell.',
    ionFlow:
      'OH⁻ ions move through the KOH electrolyte from the oxygen cathode to the hydrogen anode, where water forms.',
    energyChange: 'chemical-to-electrical',
    sources: ['crc', 'textbook'],
    reviewStatus: BATTERY_REVIEW_STATUS,
  },
];

export const UNSUPPORTED_BATTERIES: { id: string; name: string; reason: string }[] = [
  {
    id: 'lithium-iron-phosphate',
    name: 'Lithium iron phosphate (home battery)',
    reason:
      'Its positive electrode chemistry differs from LiCoO₂ and has not been reviewed for this tool yet.',
  },
  {
    id: 'zinc-air',
    name: 'Zinc–air hearing-aid cell',
    reason:
      'Its oxygen electrode and electrolyte details have not been reviewed for this tool yet.',
  },
];

export const MIN_BATTERY_CASES = 8;

/* -------------------------------------------------------------------- model */

function termText(item: BatteryTerm): string {
  return `${item.coefficient === 1 ? '' : item.coefficient}${BATTERY_SPECIES[item.species].display}`;
}

function electronText(count: number): string {
  return count === 1 ? 'e⁻' : `${count}e⁻`;
}

export function batteryHalfText(half: BatteryHalfEquation): string {
  const left = half.left.map(termText);
  const right = half.right.map(termText);
  (half.electronSide === 'left' ? left : right).push(electronText(half.electrons));
  return `${left.join(' + ')} → ${right.join(' + ')}`;
}

export function batteryFullText(equation: BatteryFullEquation): string {
  return `${equation.left.map(termText).join(' + ')} → ${equation.right.map(termText).join(' + ')}`;
}

export interface BatteryBalance {
  atomsBalanced: boolean;
  chargeBalanced: boolean;
}

function sideTotals(terms: BatteryTerm[], electrons: number) {
  const atoms: Record<string, number> = {};
  let charge = -electrons;
  terms.forEach((item) => {
    const species = BATTERY_SPECIES[item.species];
    Object.entries(species.elements).forEach(([element, count]) => {
      atoms[element] = (atoms[element] ?? 0) + count * item.coefficient;
    });
    charge += species.charge * item.coefficient;
  });
  return { atoms, charge };
}

export function batteryBalance(equation: BatteryHalfEquation | BatteryFullEquation): BatteryBalance {
  const half = 'electrons' in equation ? equation : null;
  const left = sideTotals(equation.left, half?.electronSide === 'left' ? half.electrons : 0);
  const right = sideTotals(equation.right, half?.electronSide === 'right' ? half.electrons : 0);
  const elements = new Set([...Object.keys(left.atoms), ...Object.keys(right.atoms)]);
  return {
    atomsBalanced: [...elements].every(
      (element) => (left.atoms[element] ?? 0) === (right.atoms[element] ?? 0),
    ),
    chargeBalanced: left.charge === right.charge,
  };
}

function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

/**
 * Adds the oxidation (anode) and reduction (cathode) half-equations after scaling so the electrons
 * cancel, then cancels any species that appears on both sides (such as H₂O or OH⁻).
 */
export function combineBatteryHalves(
  anode: BatteryHalfEquation,
  cathode: BatteryHalfEquation,
): BatteryFullEquation {
  if (anode.electronSide !== 'right' || cathode.electronSide !== 'left') {
    throw new Error('combineBatteryHalves needs an oxidation and a reduction half-equation');
  }
  const electrons = (anode.electrons * cathode.electrons) / gcd(anode.electrons, cathode.electrons);
  const net = new Map<BatterySpeciesId, number>();
  const order: BatterySpeciesId[] = [];
  const add = (terms: BatteryTerm[], scale: number, sign: number) => {
    terms.forEach((item) => {
      if (!net.has(item.species)) order.push(item.species);
      net.set(item.species, (net.get(item.species) ?? 0) + sign * item.coefficient * scale);
    });
  };
  const anodeScale = electrons / anode.electrons;
  const cathodeScale = electrons / cathode.electrons;
  add(anode.left, anodeScale, -1);
  add(cathode.left, cathodeScale, -1);
  add(anode.right, anodeScale, 1);
  add(cathode.right, cathodeScale, 1);
  const left: BatteryTerm[] = [];
  const right: BatteryTerm[] = [];
  order.forEach((species) => {
    const value = net.get(species) ?? 0;
    if (value < 0) left.push({ species, coefficient: -value });
    if (value > 0) right.push({ species, coefficient: value });
  });
  const divisor = [...left, ...right].reduce((acc, item) => gcd(acc, item.coefficient), 0);
  const reduce = (terms: BatteryTerm[]) =>
    terms.map((item) => ({ species: item.species, coefficient: item.coefficient / divisor }));
  return { left: reduce(left), right: reduce(right) };
}

function sideKey(terms: BatteryTerm[]): string {
  return terms
    .map((item) => `${item.coefficient}${item.species}`)
    .sort()
    .join('+');
}

export function sameBatteryEquation(a: BatteryFullEquation, b: BatteryFullEquation): boolean {
  return sideKey(a.left) === sideKey(b.left) && sideKey(a.right) === sideKey(b.right);
}

export type BatteryLookup =
  | { status: 'ok'; item: BatteryCase }
  | { status: 'unsupported'; reason: string };

export function findBatteryCase(id: string): BatteryLookup {
  const item = BATTERY_CASES.find((entry) => entry.id === id);
  if (item) return { status: 'ok', item };
  const unsupported = UNSUPPORTED_BATTERIES.find((entry) => entry.id === id);
  return {
    status: 'unsupported',
    reason: unsupported
      ? unsupported.reason
      : 'This cell is not in the reviewed list, so the model does not predict its reactions.',
  };
}

export function otherElectrode(sign: ElectrodeSign): ElectrodeSign {
  return sign === 'negative' ? 'positive' : 'negative';
}

export interface ElectronFlow {
  from: ElectrodeSign;
  to: ElectrodeSign;
  description: string;
}

/** Electrons always leave the anode and enter the cathode through the external circuit. */
export function electronFlow(item: BatteryCase): ElectronFlow {
  const from = item.anodeElectrode;
  const to = otherElectrode(from);
  const via = item.direction === 'recharge' ? 'through the charger' : 'through the load';
  return {
    from,
    to,
    description: `Electrons flow ${via} from the ${from} electrode (anode) to the ${to} electrode (cathode).`,
  };
}

export interface BatteryPredictionFeedback {
  status: 'checked';
  anodeCorrect: boolean;
  anodeMessage: string;
  categoryCorrect: boolean;
  categoryMessage: string;
}

export type BatteryPredictionResult =
  | BatteryPredictionFeedback
  | { status: 'invalid'; reason: string };

const ELECTRODE_CHOICES: ElectrodeSign[] = ['negative', 'positive'];
export const BATTERY_CATEGORIES: BatteryCategory[] = ['primary', 'secondary', 'fuel-cell'];

function anodeFeedback(item: BatteryCase, choice: ElectrodeSign): { correct: boolean; message: string } {
  const correct = choice === item.anodeElectrode;
  const half = batteryHalfText(item.anode);
  if (item.direction === 'discharge') {
    return correct
      ? {
          correct,
          message: `Right: while the cell supplies current, oxidation at the negative electrode releases electrons into the circuit, so it is the anode. ${half}`,
        }
      : {
          correct,
          message: `Not the positive electrode. A discharging cell pushes electrons out of its anode, which makes the anode the negative terminal. The positive electrode gains electrons (reduction), so it is the cathode. Anode: ${half}`,
        };
  }
  return correct
    ? {
        correct,
        message: `Right: the charger pulls electrons out of the positive electrode, so oxidation happens there and it is the anode while recharging. The + and − terminals keep their labels; anode and cathode swap compared with discharging. ${half}`,
      }
    : {
        correct,
        message: `Not the negative electrode this time. While recharging, the charger pushes electrons into the negative electrode, so reduction happens there and it is the cathode. The + and − labels stay the same, but anode and cathode swap compared with discharging. Anode: ${half}`,
      };
}

function categoryFeedback(
  item: BatteryCase,
  choice: BatteryCategory,
): { correct: boolean; message: string } {
  if (choice === item.category) return { correct: true, message: `Right: ${item.categoryReason}` };
  let prefix: string;
  if (choice === 'fuel-cell') {
    prefix = 'Not a fuel cell: its reactants are sealed inside the cell rather than fed in continuously.';
  } else if (item.category === 'fuel-cell') {
    prefix = `Not a ${choice} cell: its reactants are not stored in a sealed case but fed in continuously.`;
  } else if (item.category === 'primary') {
    prefix = 'Not rechargeable in practice.';
  } else {
    prefix = 'It can be recharged.';
  }
  return { correct: false, message: `${prefix} ${item.categoryReason}` };
}

export function evaluateBatteryPrediction(
  item: BatteryCase,
  anodeChoice: string,
  categoryChoice: string,
): BatteryPredictionResult {
  const anode = ELECTRODE_CHOICES.find((choice) => choice === anodeChoice);
  const category = BATTERY_CATEGORIES.find((choice) => choice === categoryChoice);
  if (!anode || !category) {
    return {
      status: 'invalid',
      reason: 'Choose the anode electrode and the type of cell before checking.',
    };
  }
  const anodeResult = anodeFeedback(item, anode);
  const categoryResult = categoryFeedback(item, category);
  return {
    status: 'checked',
    anodeCorrect: anodeResult.correct,
    anodeMessage: anodeResult.message,
    categoryCorrect: categoryResult.correct,
    categoryMessage: categoryResult.message,
  };
}
